import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Heart, HandHeart, Calendar, CheckCircle, Settings, Medal, Star, Crown, Gem, Bookmark, ExternalLink, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import NeedPostCard from '../components/NeedPostCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const badgeConfig = {
  bronze: { color: 'bg-amber-700', icon: Medal, label: 'Bronze' },
  silver: { color: 'bg-gray-400', icon: Medal, label: 'Silver' },
  gold: { color: 'bg-yellow-500', icon: Star, label: 'Gold' },
  platinum: { color: 'bg-slate-300 text-slate-800', icon: Crown, label: 'Platinum' },
  diamond: { color: 'bg-cyan-400', icon: Gem, label: 'Diamond' }
};

const Profile = () => {
  const { userId } = useParams();
  const { user: currentUser, token } = useAuth();
  const { t, language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [donations, setDonations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const isOwnProfile = !userId || userId === currentUser?.user_id;
  const targetUserId = userId || currentUser?.user_id;

  useEffect(() => {
    if (targetUserId) {
      fetchProfile();
      fetchUserPosts();
      fetchUserDonations();
      fetchCategories();
      if (isOwnProfile && token) {
        fetchFavorites();
      }
    }
  }, [targetUserId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${targetUserId}`);
      setProfile(response.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/user/${targetUserId}`);
      setPosts(response.data);
    } catch { /* ignore */ }
  };

  const fetchUserDonations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/donations/user/${targetUserId}`);
      setDonations(response.data);
    } catch { /* ignore */ }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch { /* ignore */ }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch { /* ignore */ }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? (language === 'it' ? cat.name_it : cat.name_en) : catId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="font-heading text-2xl font-semibold text-stone-700">Profilo non trovato</h2>
        </div>
      </div>
    );
  }

  const badge = badgeConfig[profile.badge_level] || badgeConfig.bronze;
  const BadgeIcon = badge.icon;
  const memberSince = new Date(profile.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="h-28 w-28 ring-4 ring-stone-100">
              <AvatarImage src={profile.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${profile.user_id}`} />
              <AvatarFallback className="text-3xl">{profile.name[0]}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
                <h1 className="font-heading text-3xl font-bold text-stone-900">{profile.name}</h1>
                {profile.is_verified && (
                  <Badge className="bg-blue-100 text-blue-700 w-fit mx-auto md:mx-0">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t('verified')}
                  </Badge>
                )}
              </div>
              
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-4">
                <Badge variant="secondary" className={`${badge.color}`}>
                  <BadgeIcon className="h-3 w-3 mr-1" />
                  {badge.label}
                </Badge>
                {profile.residence_city && (
                  <span className="text-stone-500 flex items-center gap-1 text-sm">
                    <MapPin className="h-3.5 w-3.5" />{profile.residence_city}
                  </span>
                )}
                <span className="text-stone-500 flex items-center gap-1 text-sm">
                  <Calendar className="h-3.5 w-3.5" />
                  {t('memberSince')}: {memberSince}
                </span>
              </div>

              {/* PayPal link */}
              {profile.paypal_address && (
                <a href={profile.paypal_address.startsWith('http') ? profile.paypal_address : `https://${profile.paypal_address}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="profile-paypal-link"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0070BA]/10 text-[#003087] rounded-full text-sm font-medium hover:bg-[#0070BA]/20 transition-colors mb-4 mr-2">
                  <span className="font-bold">P</span> PayPal <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              {profile.phone_number && (
                <a href={`tel:${profile.phone_number}`}
                  data-testid="profile-phone-link"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium hover:bg-emerald-100 transition-colors mb-4">
                  <Phone className="h-3.5 w-3.5" /> {profile.phone_number}
                </a>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 md:w-fit">
                {profile.user_type === 'donor' || profile.total_donated > 0 ? (
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-emerald-800 mb-1">
                      <HandHeart className="h-5 w-5" />
                      <span className="text-sm font-medium">{t('totalDonated')}</span>
                    </div>
                    <p className="font-heading text-2xl font-bold text-emerald-800">
                      €{profile.total_donated.toFixed(2)}
                    </p>
                  </div>
                ) : null}
                
                {profile.user_type === 'recipient' || profile.total_received > 0 ? (
                  <div className="bg-amber-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-amber-700 mb-1">
                      <Heart className="h-5 w-5" />
                      <span className="text-sm font-medium">{t('totalReceived')}</span>
                    </div>
                    <p className="font-heading text-2xl font-bold text-amber-700">
                      €{profile.total_received.toFixed(2)}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

            {isOwnProfile && (
              <Link to="/settings">
                <Button variant="outline" className="rounded-full">
                  <Settings className="h-4 w-4 mr-2" />
                  {t('settings')}
                </Button>
              </Link>
            )}
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="w-full justify-start bg-white rounded-xl p-1 mb-6">
            <TabsTrigger value="posts" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white">
              {t('myPosts')} ({posts.length})
            </TabsTrigger>
            <TabsTrigger value="donations" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white">
              {t('myDonations')} ({donations.length})
            </TabsTrigger>
            {isOwnProfile && (
              <TabsTrigger value="favorites" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white" data-testid="favorites-tab">
                <Bookmark className="h-4 w-4 mr-1" />
                {language === 'it' ? 'Preferiti' : 'Favorites'} ({favorites.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="posts">
            {posts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                <Heart className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nessuna richiesta pubblicata</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {posts.map((post, index) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <NeedPostCard post={post} getCategoryName={getCategoryName} />
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="donations">
            {donations.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                <HandHeart className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nessuna donazione effettuata</p>
              </div>
            ) : (
              <div className="space-y-4">
                {donations.map((donation, index) => (
                  <motion.div
                    key={donation.donation_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 flex items-center justify-between"
                  >
                    <div>
                      <Link to={`/posts/${donation.post_id}`} className="font-semibold text-stone-900 hover:text-emerald-800">
                        Donazione #{donation.donation_id.slice(-6)}
                      </Link>
                      {donation.message && (
                        <p className="text-sm text-stone-500 mt-1">"{donation.message}"</p>
                      )}
                      <p className="text-sm text-stone-400 mt-1">
                        {new Date(donation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-xl font-bold text-emerald-800">
                        {donation.amount.toFixed(2)} {donation.currency}
                      </p>
                      <Badge variant={donation.status === 'completed' ? 'default' : 'secondary'} className="mt-1">
                        {donation.status}
                      </Badge>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          {isOwnProfile && (
            <TabsContent value="favorites">
              {favorites.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                  <Bookmark className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">
                    {language === 'it' ? 'Nessun post salvato nei preferiti' : 'No saved favorites yet'}
                  </p>
                  <Link to="/feed">
                    <Button variant="outline" className="mt-4 rounded-full">
                      {language === 'it' ? 'Esplora il feed' : 'Explore feed'}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {favorites.map((post, index) => (
                    <motion.div key={post.post_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                      <NeedPostCard post={post} getCategoryName={getCategoryName} />
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default Profile;
